"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ReCaptchaProvider } from "@/components/providers/ReCaptchaProvider";
import { RecaptchaNotice } from "@/components/shared/RecaptchaNotice";
import { postCareersApplication } from "@/lib/careers-application-client";
import { parseNotifyEmailListFromEnvChain } from "@/lib/notify-email-list";
import { useRecaptchaToken } from "@/lib/recaptcha-client";

const NOTIFY_CONFIGURED =
  parseNotifyEmailListFromEnvChain(
    process.env.NEXT_PUBLIC_CONTACT_NOTIFY_EMAIL,
    process.env.NEXT_PUBLIC_DIGITAL_REPORT_NOTIFY_EMAIL,
  ).length > 0;

const ACCEPT_ATTR = ".pdf,.jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif,application/pdf";

const careersFormSchema = z.object({
  firstName: z.string().trim().min(1, { message: "Enter your first name" }),
  lastName: z.string().trim().min(1, { message: "Enter your last name" }),
  email: z.string().trim().email({ message: "Enter a valid email address" }),
  phone: z.string().trim(),
  message: z.string().trim(),
});

type CareersFormValues = z.infer<typeof careersFormSchema>;

const inputClass =
  "w-full border-0 border-b border-[#22242a]/20 bg-transparent px-0 py-2 text-base text-[#22242a] outline-none transition-colors placeholder:text-[#22242a]/45 focus-visible:border-b-[#ff5c00]";

export function CareersApplicationForm() {
  return (
    <ReCaptchaProvider>
      <CareersApplicationFormFields />
    </ReCaptchaProvider>
  );
}

function CareersApplicationFormFields() {
  const { getToken } = useRecaptchaToken();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CareersFormValues>({
    resolver: zodResolver(careersFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    setFileError(null);

    if (!NOTIFY_CONFIGURED) {
      setSubmitError(
        "Notification email is not configured. Add NEXT_PUBLIC_CONTACT_NOTIFY_EMAIL to your environment.",
      );
      return;
    }

    if (!resumeFile) {
      setFileError("Please upload your CV (PDF or image).");
      return;
    }

    if (resumeFile.size > 8 * 1024 * 1024) {
      setFileError("File must be 8MB or smaller.");
      return;
    }

    const recaptcha = await getToken("careers_application_submit");
    if (!recaptcha.ok) {
      setSubmitError(recaptcha.error);
      return;
    }

    const fd = new FormData();
    fd.append("firstName", values.firstName);
    fd.append("lastName", values.lastName);
    fd.append("email", values.email);
    fd.append("phone", values.phone);
    fd.append("message", values.message);
    fd.append("recaptchaToken", recaptcha.token);
    fd.append("resume", resumeFile, resumeFile.name);

    const result = await postCareersApplication(fd);

    if (result.ok) {
      setSubmitSuccess(true);
      reset();
      setResumeFile(null);
      return;
    }

    setSubmitError(result.error);
  });

  return (
    <div className="max-w-xl">
      {submitSuccess ? (
        <p
          className="rounded-lg border border-green-700/25 bg-green-50 px-4 py-3 text-sm text-green-800"
          role="status"
        >
          Thanks—we&apos;ve received your application and CV. We&apos;ll be in touch.
        </p>
      ) : (
        <form className="space-y-6" onSubmit={onSubmit} noValidate>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="careers-first-name" className="sr-only">
                First name (required)
              </label>
              <input
                id="careers-first-name"
                autoComplete="given-name"
                placeholder="First name *"
                className={inputClass}
                {...register("firstName")}
              />
              {errors.firstName ? (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {errors.firstName.message}
                </p>
              ) : null}
            </div>
            <div>
              <label htmlFor="careers-last-name" className="sr-only">
                Last name (required)
              </label>
              <input
                id="careers-last-name"
                autoComplete="family-name"
                placeholder="Last name *"
                className={inputClass}
                {...register("lastName")}
              />
              {errors.lastName ? (
                <p className="mt-1 text-xs text-red-600" role="alert">
                  {errors.lastName.message}
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <label htmlFor="careers-email" className="sr-only">
              Email (required)
            </label>
            <input
              id="careers-email"
              type="email"
              autoComplete="email"
              placeholder="Email *"
              className={inputClass}
              {...register("email")}
            />
            {errors.email ? (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.email.message}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="careers-phone" className="sr-only">
              Phone (optional)
            </label>
            <input
              id="careers-phone"
              type="tel"
              autoComplete="tel"
              placeholder="Phone (optional)"
              className={inputClass}
              {...register("phone")}
            />
          </div>

          <div>
            <label htmlFor="careers-message" className="sr-only">
              Your message
            </label>
            <textarea
              id="careers-message"
              rows={4}
              placeholder="Your message"
              className={`${inputClass} resize-none`}
              {...register("message")}
            />
          </div>

          <div className="border-b border-[#22242a]/20 pb-4">
            <p className="mb-1 text-sm font-medium text-[#22242a]/85">Upload your CV *</p>
            <input
              id="careers-resume"
              type="file"
              accept={ACCEPT_ATTR}
              className="block w-full max-w-md text-sm text-[#22242a] file:mr-3 file:rounded-full file:border-0 file:bg-[#ff5c00] file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-[#ea4f10]"
              onChange={(e) => {
                setFileError(null);
                const file = e.target.files?.[0] ?? null;
                setResumeFile(file);
              }}
            />
            {resumeFile ? (
              <p className="mt-2 text-xs text-[#22242a]/65">Selected: {resumeFile.name}</p>
            ) : null}
            {fileError ? (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {fileError}
              </p>
            ) : null}
            <p className="mt-2 text-xs text-[#22242a]/55">PDF or image (JPEG, PNG, WebP, GIF). Max 8MB.</p>
          </div>

          {submitError ? (
            <p className="rounded-lg border border-red-700/25 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              {submitError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#ff5c00] px-8 text-base font-medium text-white transition-colors hover:bg-[#ea4f10] disabled:opacity-60"
          >
            {isSubmitting ? "Sending…" : "Submit application"}
          </button>
          <RecaptchaNotice
            className="text-xs text-[#22242a]/60"
            linkClassName="underline underline-offset-2 hover:text-[#22242a]"
          />
        </form>
      )}
    </div>
  );
}

